/** Экраны 6, 7, вход и уведомления. */
import type { ProfileField } from '@allswipes/client';
import { ApiError } from '@allswipes/client';
import { Badge, Button, Card, Empty, Field, KeyRow, Progress, REGIONS, Skeleton, ThemeToggle, fmtShortDay, num } from '@allswipes/ui';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import { api } from '../api';
import { Shell } from '../components/Shell';
import { SITE_URL } from '../site';
import { useResource } from '../hooks';
import { useSession } from '../session';
import { useToast } from '../toast';

import { usePlural, useT } from '@allswipes/i18n';

// ─── знакомство: то, что нужно спросить один раз ───

/**
 * Экран после первого входа — любым способом.
 *
 * Стоит отдельным адресом, а не куском экрана входа, потому что входов два:
 * код на почту и Google. Человек, пришедший через Google, нажал одну кнопку —
 * и раньше оказывался в приложении без имени, которое понадобится у стойки
 * выдачи приза. Спрашивать его потом значит спрашивать посреди участия в
 * акции.
 *
 * Что уже известно — показано заполненным: имя и фамилию отдаёт Google, и
 * переписывать их человеку незачем. Поправить при этом можно всё: у Google
 * могло остаться старое имя, а приз выдают по документу.
 *
 * Ничего не спросив, экран уходит дальше молча: пустая форма «всё уже
 * заполнено, нажми продолжить» — это лишний шаг, показанный ради симметрии.
 */
export function WelcomeScreen() {
  const t = useT();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get('next') || '/';
  const { refresh } = useSession();

  const [fields, setFields] = useState<ProfileField[] | null>(null);
  const [values, setValues] = useState<Record<string, string>>({});
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    void (async () => {
      try {
        const profile = await api.profileData();
        if (!alive) return;
        const basics = profile.fields.filter((f) => f.basic);
        if (basics.every((f) => (profile.values[f.key] ?? '') !== '')) {
          navigate(next, { replace: true });
          return;
        }
        setValues(Object.fromEntries(basics.map((f) => [f.key, profile.values[f.key] ?? ''])));
        setFields(basics);
      } catch {
        // Анкета не прочиталась — это не повод держать человека на пустом
        // экране: он уже вошёл, и спросить можно в профиле.
        if (alive) navigate(next, { replace: true });
      }
    })();
    return () => {
      alive = false;
    };
  }, [navigate, next]);

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.saveProfileData(values);
      await refresh();
      navigate(next, { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        setFieldErrors(err.fields ?? {});
        setError(err.message);
      } else {
        setError(t('Не получилось сохранить'));
      }
    } finally {
      setBusy(false);
    }
  };

  if (!fields) {
    return (
      <Shell title={t('Пара слов о тебе')}>
        <Card>
          <Skeleton count={3} />
        </Card>
      </Shell>
    );
  }

  const filled = fields.every((f) => (values[f.key] ?? '').trim() !== '');
  return (
    <Shell title={t('Пара слов о тебе')}>
      <Card>
        <p className="sub" style={{ marginBottom: 14 }}>
          {t('Это нужно при выдаче приза: как к тебе обратиться и как записано в документе. Остальное бренд спросит сам, если его акции это нужно, — и ты увидишь, что именно передаёшь.')}
        </p>

        {fields.map((field) => (
          <Field
            key={field.key}
            label={field.label}
            hint={field.hint ?? field.why}
            error={fieldErrors[field.key]}
          >
            {field.options && field.options.length > 0 ? (
              <select
                className="input"
                value={values[field.key] ?? ''}
                onChange={(e) => setValues({ ...values, [field.key]: e.target.value })}
              >
                <option value="">{t('Выбери')}</option>
                {field.options.map((option) => (
                  <option key={option} value={option}>
                    {t(option)}
                  </option>
                ))}
              </select>
            ) : (
              <input
                className="input"
                value={values[field.key] ?? ''}
                autoComplete={
                  field.key === 'name' ? 'given-name' : field.key === 'surname' ? 'family-name' : 'off'
                }
                onChange={(e) => setValues({ ...values, [field.key]: e.target.value })}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && filled) void save();
                }}
              />
            )}
          </Field>
        ))}

        {error && (
          <p className="sub" style={{ color: 'var(--bad)', marginBottom: 10 }}>
            {error}
          </p>
        )}

        <Button variant="pri" block disabled={busy || !filled} onClick={save}>
          {busy ? t('Сохраняем…') : t('Продолжить')}
        </Button>

        <p className="sub" style={{ marginTop: 12 }}>
          {t('Эти данные остаются в твоём профиле и никуда не уходят сами. Бренд получит их, только если его акция их запросит и ты это подтвердишь.')}
        </p>
      </Card>
    </Shell>
  );
}

// ─── вход по почте ───

export function LoginScreen() {
  const t = useT();
  const [params] = useSearchParams();
  const next = params.get('next') ?? '/';
  const navigate = useNavigate();
  const { refresh } = useSession();

  const catalog = useResource(() => api.catalog(), []);
  // Google вернул человека с отказом — показываем причину на том же экране,
  // где он нажимал кнопку, а не отдельной страницей ошибки.
  const googleError = params.get('google');
  const [email, setEmail] = useState('');
  const [region, setRegion] = useState('TJ');
  const [challenge, setChallenge] = useState<{
    id: string;
    devCode?: string;
    sentTo?: string;
    expiresAt: number;
  } | null>(null);
  // Часы тикают раз в секунду, пока на экране ждут код. Срок кода — обещание
  // из письма («действует 5 минут»), и человек должен видеть, сколько у него
  // осталось, а не гадать, ждать письмо ещё или просить новое.
  const [now, setNow] = useState(() => Date.now());
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Тикаем, только пока экран ждёт код и он ещё не истёк: таймер, который
  // продолжает считать после истечения, жжёт батарею ни за чем.
  const codeLeft = challenge ? Math.max(0, challenge.expiresAt - now) : 0;
  const codeExpired = Boolean(challenge) && codeLeft === 0;
  useEffect(() => {
    if (!challenge || codeExpired) return;
    const tick = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(tick);
  }, [challenge, codeExpired]);

  const request = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await api.requestCode(email);
      setChallenge({
        id: res.challenge_id,
        devCode: res.dev_code,
        sentTo: res.sent_to,
        expiresAt: new Date(res.expires_at).getTime(),
      });
      setCode(res.dev_code ?? '');
      setNow(Date.now());
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('Не получилось отправить код'));
    } finally {
      setBusy(false);
    }
  };

  const verify = async () => {
    if (!challenge) return;
    setBusy(true);
    setError(null);
    try {
      await api.verifyOtp(challenge.id, code, region);
      await refresh();
      // Дальше — экран знакомства: он сам решит, спрашивать ли что-то, и
      // уйдёт дальше молча, если анкета уже заполнена. Решение принимается
      // по данным, а не по признаку «аккаунт только что создан»: человек мог
      // прерваться на этом шаге в прошлый раз, и спросить второй раз честнее,
      // чем не спросить вовсе.
      navigate(`/welcome?next=${encodeURIComponent(next)}`, { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('Не получилось войти'));
    } finally {
      setBusy(false);
    }
  };

  // Telegram пока не работает: кнопка на месте и честно говорит об этом.
  // Прятать её нельзя — человек должен знать, что способ появится.
  const telegramSoon = () => setError(t('Вход через Telegram скоро появится. Пока входи по почте.'));

  return (
    <Shell title={t('Вход и регистрация')}>
      <Card>
        {!challenge ? (
          <>
            {/*
              Отдельной регистрации в продукте нет: первый введённый код и
              заводит аккаунт, и впускает. Две вкладки «вход» и «регистрация»
              показывали бы одну и ту же форму — поэтому экран один, но он
              обязан сказать обе вещи вслух. Человек, попавший сюда впервые,
              иначе решит, что сначала нужен аккаунт, и уйдёт.
            */}
            <p className="sub" style={{ marginBottom: 6 }}>
              {t('Отдельной регистрации нет: введи почту — если аккаунта ещё нет, он заведётся сам.')}
            </p>
            <p className="sub" style={{ marginBottom: 14 }}>
              {t('Пароль не нужен, на почту придёт код. Почта привязывает твои билеты и призы, и без неё нельзя сменить страну.')}
            </p>
            {googleError && (
              <p className="sub" style={{ color: 'var(--bad)', marginBottom: 12 }} role="alert">
                {googleError}
              </p>
            )}
            <Field label={t('Почта')} error={error ?? undefined}>
              <input
                className="input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') void request();
                }}
                inputMode="email"
                autoComplete="email"
                placeholder="you@example.com"
              />
            </Field>
            <Field label={t('Страна')} hint={t('Лента покажет акции, которые идут здесь.')}>
              <select className="select" value={region} onChange={(e) => setRegion(e.target.value)}>
                {REGIONS.map((r) => (
                  <option key={r.code} value={r.code}>
                    {t(r.name)}
                  </option>
                ))}
              </select>
            </Field>
            <Button variant="pri" block disabled={busy} onClick={request}>
              {busy ? t('Отправляем…') : t('Продолжить по почте')}
            </Button>

            <div className="orline">{t('или')}</div>
            {/*
              Google внизу и всегда на виду: он решает обе задачи сразу — и
              вход, и заведение аккаунта, — и человеку, у которого аккаунта
              нет, это самый короткий путь. Кнопка появляется только там, где
              вход настроен: кнопка, которая всегда отвечает ошибкой, хуже
              отсутствующей. Адрес возврата собирает сервер, чтобы подписанное
              состояние и секрет не оказались в браузере.
            */}
            {catalog.data?.auth?.google && (
              <>
                <a
                  className="btn out block"
                  href={`${api.baseUrl}/api/v1/auth/google/start?next=${encodeURIComponent(next)}`}
                >
                  {t('Продолжить с Google')}
                </a>
                <p className="sub" style={{ marginTop: 8, fontSize: 12 }}>
                  {t('Тоже и вход, и регистрация. Если на этой почте уже есть аккаунт, попадёшь в него — билеты и баллы на месте.')}
                </p>
              </>
            )}
            <Button variant="out" block icon="external" onClick={telegramSoon}>
              {t('Войти через Telegram · скоро')}
            </Button>
            <p className="sub" style={{ marginTop: 12 }}>
              {t('Номер телефона можно добавить в профиле после входа. Подтверждение номера появится позже — тогда бренды смогут запрашивать подтверждённый номер как условие акции.')}
            </p>
            {/*
              Ссылка, а не развилка в начале: компаний единицы на тысячи
              участников, и заставлять каждого выбирать, кто он, ради этих
              единиц — это шаг, который девяносто девять человек из ста
              проходят зря.
            */}
            <p className="sub" style={{ marginTop: 10 }}>
              <Link to="/for-companies">{t('Я компания — хочу проводить акции')}</Link>
            </p>
          </>
        ) : (
          <>
            <p className="sub" style={{ marginBottom: 6 }}>
              {t('Код отправлен на {to}. Введи шесть цифр.', { to: challenge.sentTo ?? email })}
            </p>
            <p
              className="sub mono"
              style={{ marginBottom: 14, color: codeExpired ? 'var(--bad)' : undefined }}
            >
              {codeExpired
                ? t('Срок кода истёк — запроси новый.')
                : t('Код действует ещё {left}', { left: mmss(codeLeft) })}
            </p>
            {challenge.devCode && (
              <p className="sub" style={{ marginBottom: 12, color: 'var(--warn)' }}>
                {t('Режим разработки: код {code} подставлен автоматически. В бою он приходит письмом и в ответе API не появляется.', { code: challenge.devCode })}
              </p>
            )}
            <Field label={t('Код из письма')} error={error ?? undefined}>
              <input
                className="input mono codein"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !codeExpired) void verify();
                }}
                inputMode="numeric"
                maxLength={6}
                autoComplete="one-time-code"
                disabled={codeExpired}
              />
            </Field>
            {codeExpired ? (
              <Button variant="pri" block disabled={busy} onClick={request}>
                {busy ? t('Отправляем…') : t('Отправить код снова')}
              </Button>
            ) : (
              <Button variant="pri" block disabled={busy || code.length < 4} onClick={verify}>
                {busy ? t('Проверяем…') : t('Войти')}
              </Button>
            )}
            <div style={{ marginTop: 10 }}>
              <Button variant="ghost" block onClick={() => setChallenge(null)}>
                {t('Изменить почту')}
              </Button>
            </div>
          </>
        )}
      </Card>
    </Shell>
  );
}

// mmss — «4:37». Обратный отсчёт до конца жизни кода.
function mmss(ms: number): string {
  const total = Math.ceil(ms / 1000);
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
}

// ─── баллы ───

export function PointsScreen() {
  const t = useT();
  const { signedIn } = useSession();
  const points = useResource(
    () => (signedIn ? api.points() : Promise.resolve(null)),
    [signedIn],
  );

  if (!signedIn) {
    return (
      <Shell wide title={t('Баллы')}>
        <Empty icon="coins" title={t('Войди, чтобы видеть баллы')}>
          <Link to="/login?next=/points" className="btn pri" style={{ marginTop: 12 }}>
            {t('Войти')}
          </Link>
        </Empty>
      </Shell>
    );
  }

  return (
    <Shell
      wide
      title={t('Баллы')}
      subtitle={t('начисляются за каждый принятый код — баланс всегда равен сумме истории')}
    >
      {points.loading && <Skeleton count={2} height={100} />}
      {points.data && (
        <div className="duo">
          <div className="stack">
          <Card>
            <div className="pts">
              <div>
                <div className="lab">{t('Баланс')}</div>
                <div className="val">{num(points.data.balance)}</div>
              </div>
              <div style={{ flex: 1 }}>
                <div className="row" style={{ marginBottom: 6 }}>
                  <Badge tone="prize">{t(points.data.rank.name)}</Badge>
                  <span className="spacer" />
                  {points.data.next_rank && (
                    <span className="faint" style={{ fontSize: 11.5 }}>
                      {t('до «{rank}» — {left}', {
                        rank: t(points.data.next_rank.name),
                        left: num(points.data.next_rank.from - points.data.balance),
                      })}
                    </span>
                  )}
                </div>
                {points.data.next_rank && (
                  <Progress
                    value={points.data.balance - points.data.rank.from}
                    max={Math.max(1, points.data.next_rank.from - points.data.rank.from)}
                  />
                )}
              </div>
            </div>
            <p className="sub" style={{ marginTop: 12 }}>
              {t('Баллы начисляются за каждый принятый код. Баланс — это сумма всей истории ниже, а не отдельное число: так он не может разойтись с операциями.')}
            </p>
          </Card>

          <Card>
            <div className="h2">{t('Обмен баллов')}</div>
            <p className="sub" style={{ marginTop: 6 }}>
              {t('Витрина обмена — этап 3 в плане переноса. Пока баллы копятся и видны бренду как показатель вовлечения.')}
            </p>
          </Card>
          </div>

          <div className="stack">
          <Card>
            <div className="h2" style={{ marginBottom: 10 }}>
              {t('История')}
            </div>
            {points.data.ledger.length === 0 ? (
              <Empty title={t('Пока пусто')} />
            ) : (
              points.data.ledger.map((entry) => (
                <div className="krow" key={entry.id}>
                  <span className="k">
                    {entry.reason}
                    <span className="faint" style={{ display: 'block', fontSize: 11 }}>
                      {fmtShortDay(entry.created_at)}
                    </span>
                  </span>
                  <span
                    className="v mono"
                    style={{ color: entry.delta > 0 ? 'var(--head)' : 'var(--dim)' }}
                  >
                    {entry.delta > 0 ? '+' : ''}
                    {num(entry.delta)}
                  </span>
                </div>
              ))
            )}
          </Card>
          </div>
        </div>
      )}
    </Shell>
  );
}

// ─── профиль ───

export function ProfileScreen() {
  const plural = usePlural();
  const t = useT();
  const { profile, signedIn, signOut, refresh } = useSession();
  const toast = useToast();
  const navigate = useNavigate();
  // Справочник областей приходит с сервера на языке интерфейса: это имена
  // собственные, и в словаре приложения им делать нечего.
  const catalog = useResource(() => api.catalog(), []);
  const subregions = catalog.data?.subregions?.[profile?.user.region ?? ''] ?? [];

  if (!signedIn || !profile) {
    return (
      <Shell wide title={t('Профиль')}>
        <Empty icon="user" title={t('Ты не вошёл')}>
          <Link to="/login?next=/profile" className="btn pri" style={{ marginTop: 12 }}>
            {t('Войти по почте')}
          </Link>
        </Empty>
      </Shell>
    );
  }

  const changeRegion = async (region: string) => {
    try {
      await api.setRegion(region);
      await refresh();
      toast(t('Регион обновлён'));
    } catch (err) {
      toast(err instanceof ApiError ? err.message : t('Не получилось'));
    }
  };

  const changeSubregion = async (subregion: string) => {
    try {
      await api.setSubregion(subregion);
      await refresh();
      toast(t('Область обновлена'));
    } catch (err) {
      toast(err instanceof ApiError ? err.message : t('Не получилось'));
    }
  };

  return (
    <Shell
      wide
      title={t('Профиль')}
      subtitle={t('вход, регион, анкета и согласия — видно, кому и какие данные ушли')}
    >
      <div className="duo">
        <div className="stack">
        <Card>
          <KeyRow label={t('Вход')} mono>
            {profile.user.login}
          </KeyRow>
          <KeyRow label={t('В публичных списках')}>{profile.user.display_handle}</KeyRow>
          <KeyRow label={t('Ранг')}>{profile.rank.name}</KeyRow>
          <KeyRow label={t('Баллы')}>{num(profile.points)}</KeyRow>
          <KeyRow label={t('Билетов')}>
            {num(profile.tickets)} {plural(profile.tickets, 'билет', 'билета', 'билетов')}
          </KeyRow>
          <KeyRow label={t('Выигрышей')}>{num(profile.wins)}</KeyRow>
          <p className="sub" style={{ marginTop: 12 }}>
            {t('В открытых списках участников виден только номер билета и обезличенная метка — ни почта, ни номер не публикуются никогда.')}
          </p>
        </Card>

        <Contacts />

        <Card>
          <Field
            label={t('Страна')}
            hint={
              profile.user.email_verified
                ? t('Лента показывает акции, которые идут в твоей стране.')
                : t('Сменить страну можно только с подтверждённой почтой: страна определяет, в каких акциях ты участвуешь.')
            }
          >
            <select
              className="select"
              value={profile.user.region}
              disabled={!profile.user.email_verified}
              onChange={(e) => void changeRegion(e.target.value)}
            >
              {REGIONS.map((r) => (
                <option key={r.code} value={r.code}>
                  {t(r.name)}
                </option>
              ))}
            </select>
          </Field>

          {/* Область показывается только там, где делить есть на что:
              у Сингапура «область Сингапура» никто не ищет. Пункт «не
              указана» остаётся всегда — поле необязательное, и отказ от него
              не должен прятать ленту. */}
          {subregions.length > 0 && (
            <Field
              label={t('Область')}
              hint={t('Акции, объявленные по штатам и областям, покажутся те, что идут у тебя. Остальные идут по всей стране.')}
            >
              <select
                className="select"
                value={profile.user.subregion ?? ''}
                onChange={(e) => void changeSubregion(e.target.value)}
              >
                <option value="">{t('не указана')}</option>
                {subregions.map((sub) => (
                  <option key={sub.code} value={sub.code}>
                    {sub.name}
                  </option>
                ))}
              </select>
            </Field>
          )}

          <div className="row" style={{ marginTop: 4 }}>
            <span className="h2">{t('Тема')}</span>
            <span className="spacer" />
            <ThemeToggle />
          </div>
        </Card>

        <Card>
          <div className="h2">{t('Кабинет бренда')}</div>
          <p className="sub" style={{ marginTop: 6 }}>
            {t('Акции создаются в отдельном кабинете партнёра. Доступ выдаёт владелец бренда.')}
          </p>
          <div style={{ marginTop: 12 }}>
            <a className="btn out block" href={SITE_URL}>
              {t('О проекте')}
            </a>
          </div>
        </Card>

        <Button
          variant="danger"
          block
          onClick={() => {
            signOut();
            navigate('/');
          }}
        >
          {t('Выйти')}
        </Button>
        </div>

        <div className="stack">
          <ProfileForm />
        </div>
      </div>
    </Shell>
  );
}

// ─── контакты и анкета ───

/**
 * Контакты профиля.
 *
 * Телефон и Telegram пока только хранятся: входа по ним нет, подтверждения
 * тоже. Писать «подтверждено» там, где ничего не проверено, нельзя — поэтому
 * рядом стоит честное «скоро», и там же сказано, что это даст.
 */
function Contacts() {
  const t = useT();
  const { profile, refresh } = useSession();
  const toast = useToast();
  const [phone, setPhone] = useState(profile?.user.phone ?? '');
  const [telegram, setTelegram] = useState(profile?.user.telegram ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!profile) return null;
  const dirty = phone !== (profile.user.phone ?? '') || telegram !== (profile.user.telegram ?? '');

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.saveContacts(phone, telegram);
      await refresh();
      toast('Контакты сохранены');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('Не получилось сохранить'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card>
      <div className="h2" style={{ marginBottom: 10 }}>
        {t('Контакты')}
      </div>

      <div className="row" style={{ gap: 8, marginBottom: 10 }}>
        <Badge tone={profile.user.email_verified ? 'good' : 'warn'}>
          {profile.user.email_verified ? t('почта подтверждена') : t('почта не подтверждена')}
        </Badge>
        <span className="mono faint" style={{ fontSize: 12 }}>
          {profile.user.email || profile.user.login}
        </span>
      </div>

      <Field label={t('Телефон')} hint={t('Пригодится для связи по призу')} error={error ?? undefined}>
        <input
          className="input mono"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          inputMode="tel"
          autoComplete="tel"
          placeholder="+992 44 700-00-01"
        />
      </Field>
      <div className="row" style={{ gap: 8, margin: '-4px 0 12px' }}>
        <Badge>{t('подтверждение — скоро')}</Badge>
        <span className="sub" style={{ fontSize: 12 }}>
          {t('Подтверждённый номер бренды смогут требовать как условие акции.')}
        </span>
      </div>

      <Field label="Telegram" hint={t('Без «@»')}>
        <input
          className="input"
          value={telegram}
          onChange={(e) => setTelegram(e.target.value)}
          placeholder="username"
        />
      </Field>

      <Button variant="pri" disabled={busy || !dirty} onClick={save}>
        {busy ? t('Сохраняем…') : t('Сохранить контакты')}
      </Button>
    </Card>
  );
}

/**
 * Анкета: восемь полей каталога и журнал того, кому они уже переданы.
 *
 * Заполнить поле и отдать его бренду — разные действия. Здесь только первое:
 * передача происходит на экране согласия, в момент участия в конкретной акции.
 */
function ProfileForm() {
  const t = useT();
  const data = useResource(() => api.profileData(), []);
  const toast = useToast();
  const [values, setValues] = useState<Record<string, string>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (data.data) {
      setValues(data.data.values);
      setTouched(false);
    }
  }, [data.data]);

  if (data.loading) return <Skeleton height={120} />;
  if (!data.data) return null;

  const save = async () => {
    setBusy(true);
    setErrors({});
    try {
      await api.saveProfileData(values);
      toast('Анкета сохранена');
      setTouched(false);
      data.reload();
    } catch (err) {
      if (err instanceof ApiError) {
        setErrors(err.fields ?? {});
        toast(err.message);
      } else {
        toast('Не получилось сохранить');
      }
    } finally {
      setBusy(false);
    }
  };

  const filled = data.data.fields.filter((f) => (values[f.key] ?? '') !== '').length;

  return (
    <>
      <Card>
        <div className="row" style={{ marginBottom: 6 }}>
          <span className="h2">{t('Анкета')}</span>
          <span className="spacer" />
          <span className="faint mono" style={{ fontSize: 12 }}>
            {filled}/{data.data.fields.length}
          </span>
        </div>
        <p className="sub" style={{ marginBottom: 12 }}>
          {t('Заполнять не обязательно. Эти поля никуда не уходят сами: бренд получит только то, что ты отдельно подтвердишь при участии в его акции.')}
        </p>

        <div className="stack">
          {data.data.fields.map((field) => (
            <Field
              key={field.key}
              label={field.label}
              hint={field.hint ?? field.why}
              error={errors[field.key]}
            >
              {field.kind === 'select' ? (
                <select
                  className="select"
                  value={values[field.key] ?? ''}
                  onChange={(e) => {
                    setValues({ ...values, [field.key]: e.target.value });
                    setTouched(true);
                  }}
                >
                  <option value="">{t('Не указано')}</option>
                  {(field.options ?? []).map((o) => (
                    <option key={o} value={o}>
                      {o}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  className={`input ${field.kind === 'date' ? 'mono' : ''}`}
                  value={values[field.key] ?? ''}
                  placeholder={field.kind === 'date' ? '14.03.1992' : ''}
                  inputMode={field.kind === 'email' ? 'email' : 'text'}
                  onChange={(e) => {
                    setValues({ ...values, [field.key]: e.target.value });
                    setTouched(true);
                  }}
                />
              )}
            </Field>
          ))}
        </div>

        <Button variant="pri" disabled={busy || !touched} onClick={save}>
          {busy ? t('Сохраняем…') : t('Сохранить анкету')}
        </Button>
      </Card>

      <Card>
        <div className="h2" style={{ marginBottom: 6 }}>
          {t('Кому ты передал данные')}
        </div>
        {data.data.consents.length === 0 ? (
          <p className="sub">
            {t('Пока никому. Когда акция попросит данные, ты увидишь список полей и их значения до того, как подтвердишь передачу.')}
          </p>
        ) : (
          <div className="stack">
            {data.data.consents.map((consent) => (
              <div key={consent.campaign_id} className="krow col">
                <span className="k">
                  {consent.brand_name} · {consent.campaign_name}
                </span>
                <span className="v">
                  {consent.fields
                    .map((key) => data.data!.fields.find((f) => f.key === key)?.label ?? key)
                    .join(', ')}
                  <span className="faint"> · {fmtShortDay(consent.created_at)}</span>
                </span>
              </div>
            ))}
          </div>
        )}
      </Card>
    </>
  );
}

// ─── уведомления ───

export function NotificationsScreen() {
  const t = useT();
  const { signedIn } = useSession();
  const list = useResource(
    () => (signedIn ? api.notifications() : Promise.resolve({ notifications: [], unread: 0 })),
    [signedIn],
  );
  const [cleared, setCleared] = useState(false);

  const markRead = async () => {
    await api.markNotificationsRead();
    setCleared(true);
    list.reload();
  };

  return (
    <Shell
      title={t('Уведомления')}
      action={
        !cleared && (list.data?.unread ?? 0) > 0 ? (
          <Button size="sm" onClick={markRead}>
            {t('Прочитано')}
          </Button>
        ) : null
      }
    >
      {list.loading && <Skeleton count={3} height={70} />}
      {list.data && list.data.notifications.length === 0 && (
        <Empty icon="bell" title={t('Пока тихо')}>
          {t('Напомним о старте акции, о розыгрыше за день и о результате.')}
        </Empty>
      )}
      <div className="stack">
        {list.data?.notifications.map((n) => (
          <Card key={n.id} className="tight">
            <div className="row">
              <span className="h2">{n.title}</span>
              <span className="spacer" />
              {!n.read_at && <Badge tone="prize">{t('новое')}</Badge>}
            </div>
            <p className="sub" style={{ marginTop: 5 }}>
              {n.body}
            </p>
            <div className="row" style={{ marginTop: 8 }}>
              <span className="faint" style={{ fontSize: 11.5 }}>
                {fmtShortDay(n.created_at)}
              </span>
              <span className="spacer" />
              {n.campaign_id && (
                <Link to={`/c/${n.campaign_id}`} className="faint" style={{ fontSize: 12 }}>
                  {t('К акции →')}
                </Link>
              )}
            </div>
          </Card>
        ))}
      </div>
    </Shell>
  );
}
